import { useState, useEffect } from "react";
import {
  ListBulletIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { classNames, formatDateAgoString } from "@via/utils/helpers";
import type { Conversation } from "@via/utils/interfaces";
import DeleteModal from "@via/components/delete-modal/delete-modal";
import IndependentPagination from "@via/components/pagination";
import { useDebounceValue } from "usehooks-ts";
import { StarIcon } from "@heroicons/react/24/solid";

interface ConversationListProps {
  conversations: Conversation[];
  activeConversationId: string | null;
  onSelectConversation: (id: string) => void;
  onDeleteConversation: (id: string) => void;
  onCreateNew: () => void;
  isOpen: boolean;
  onClose: () => void;
  onRenameConversation: (id: string, title: string | null) => Promise<void>;
  onTogglePinned: (id: string, nextPinned: boolean) => Promise<void> | void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  searchQuery: string;
  onSearchQueryChange: (query: string) => void;
  pinnedOnly: boolean;
  onPinnedOnlyChange: (value: boolean) => void;
}

interface DeleteConversationItem {
  id: string;
  name: string;
}

export function ConversationList({
  conversations,
  activeConversationId,
  onSelectConversation,
  onDeleteConversation,
  onCreateNew,
  isOpen,
  onClose,
  onRenameConversation,
  onTogglePinned,
  page,
  pageSize,
  total,
  onPageChange,
  onPageSizeChange,
  searchQuery,
  onSearchQueryChange,
  pinnedOnly,
  onPinnedOnlyChange,
}: ConversationListProps) {
  const [searchInput, setSearchInput] = useState(searchQuery);
  const [debouncedSearchInput] = useDebounceValue(searchInput, 300);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");
  const [deleteTarget, setDeleteTarget] =
    useState<DeleteConversationItem | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);

  // Keep local input in sync when external searchQuery changes (e.g. reset)
  useEffect(() => {
    setSearchInput(searchQuery);
  }, [searchQuery]);

  // Trigger backend search only when debounced value changes
  useEffect(() => {
    if (debouncedSearchInput === searchQuery) return;
    onSearchQueryChange(debouncedSearchInput);
    onPageChange(0);
  }, [debouncedSearchInput, onSearchQueryChange, onPageChange, searchQuery]);

  const getConversationTitle = (conversation: Conversation): string => {
    if (conversation.isPending) {
      return "New Conversation";
    }
    return conversation.title || "Untitled Conversation";
  };

  const getConversationPreview = (conversation: Conversation): string => {
    const firstUserMessage = conversation.conversationHistory.find(
      m => m.role === "user"
    );
    if (firstUserMessage) {
      const preview = firstUserMessage.content.substring(0, 60);
      return preview.length < firstUserMessage.content.length
        ? `${preview}...`
        : preview;
    }
    return "No messages yet";
  };

  const filteredAndSorted = conversations
    // Hide empty pending conversations (created locally but never used)
    .filter(conv => {
      const hasNoHistory = !conv.conversationHistory?.length;
      if (conv.isPending && hasNoHistory) {
        return false;
      }
      return true;
    });

  const handleSelectConversation = (id: string) => {
    onSelectConversation(id);
    onClose();
    setSearchInput("");
  };

  const handleDelete = (e: React.MouseEvent, conversationId: string) => {
    e.stopPropagation();
    const conversation = conversations.find(c => c.id === conversationId);
    const hasHistory =
      conversation && conversation.conversationHistory.length > 0;
    const title = conversation ? getConversationTitle(conversation) : "";

    // If conversation has no history, delete immediately without confirmation
    if (!hasHistory) {
      void onDeleteConversation(conversationId);
      return;
    }

    // Otherwise, open shared DeleteModal for confirmation
    setDeleteTarget({
      id: conversationId,
      name: title || "Untitled Conversation",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setIsDeleteLoading(true);
      await onDeleteConversation(deleteTarget.id);
    } finally {
      setIsDeleteLoading(false);
      setDeleteTarget(null);
    }
  };

  const startEditing = (conversation: Conversation) => {
    setEditingId(conversation.id);
    setDraftTitle(getConversationTitle(conversation));
  };

  const cancelEditing = () => {
    setEditingId(null);
    setDraftTitle("");
  };

  const commitEditing = async () => {
    if (!editingId) return;
    const trimmed = draftTitle.trim();
    const nextTitle = trimmed === "" ? null : trimmed;
    await onRenameConversation(editingId, nextTitle);
    cancelEditing();
  };

  if (!isOpen) return null;

  return (
    <div className="flex-1 flex flex-col bg-white overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-900">Conversations</h3>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => onPinnedOnlyChange(!pinnedOnly)}
              className={classNames(
                "flex items-center gap-1 rounded px-2 py-1 text-xs border",
                pinnedOnly
                  ? "border-indigo-500 bg-indigo-50 text-indigo-700"
                  : "border-gray-300 text-gray-600 hover:bg-gray-100"
              )}>
              <StarIcon
                className={classNames(
                  "h-4 w-4",
                  pinnedOnly ? "text-yellow-400" : "text-gray-400"
                )}
              />
              <span>Pinned only</span>
            </button>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-3">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={searchInput}
            onChange={e => {
              const value = e.target.value;
              setSearchInput(value);
            }}
            placeholder="Search conversations..."
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
        </div>

        {/* New Conversation Button */}
        <button
          onClick={() => {
            onCreateNew();
            onClose();
            setSearchInput("");
            onSearchQueryChange("");
            onPageChange(0);
          }}
          className="mt-3 w-full px-3 py-2 text-sm font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-colors">
          + New Conversation
        </button>
      </div>

      {/* Conversations List */}
      <div className="flex-1 overflow-y-auto p-4">
        {filteredAndSorted.length === 0 ? (
          <div className="p-4 text-center text-sm text-gray-500">
            {searchInput ? "No conversations found" : "No conversations yet"}
          </div>
        ) : (
          <div className="space-y-1">
            {filteredAndSorted.map(conversation => {
              const isActive = conversation.id === activeConversationId;
              const title = getConversationTitle(conversation);
              const preview = getConversationPreview(conversation);
              const isEditing = editingId === conversation.id;

              return (
                <div
                  key={conversation.id}
                  onClick={() => handleSelectConversation(conversation.id)}
                  className={classNames(
                    "group relative p-3 rounded-lg cursor-pointer transition-colors",
                    isActive
                      ? "bg-indigo-50 border border-indigo-200"
                      : "hover:bg-gray-50 border border-transparent"
                  )}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {isEditing ? (
                          <input
                            value={draftTitle}
                            onChange={e => setDraftTitle(e.target.value)}
                            onBlur={commitEditing}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                void commitEditing();
                              }
                              if (e.key === "Escape") {
                                e.preventDefault();
                                cancelEditing();
                              }
                            }}
                            onClick={e => {
                              e.stopPropagation();
                            }}
                            className="w-full max-w-[180px] rounded border border-indigo-300 px-2 py-0.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500 bg-white"
                            autoFocus
                          />
                        ) : (
                          <h4
                            className={classNames(
                              "text-sm font-medium truncate",
                              isActive ? "text-indigo-900" : "text-gray-900"
                            )}>
                            {title}
                          </h4>
                        )}
                        {conversation.isPending && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                            New
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 truncate mb-1">
                        {preview}
                      </p>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>
                          {formatDateAgoString(conversation.updatedAt)}
                        </span>
                        {conversation.isPinned && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-yellow-50 px-2 py-0.5 text-[10px] font-medium text-yellow-700 border border-yellow-200">
                            <StarIcon className="h-3 w-3 text-yellow-500" />
                            Pinned
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          startEditing(conversation);
                        }}
                        className={classNames(
                          "flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                          "hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        )}
                        aria-label={`Rename ${title}`}
                        title="Rename conversation">
                        <PencilSquareIcon className="h-4 w-4 text-gray-400" />
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          void onTogglePinned(
                            conversation.id,
                            !conversation.isPinned
                          );
                        }}
                        className={classNames(
                          "flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                          "hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        )}
                        aria-label={
                          conversation.isPinned
                            ? `Unpin ${title}`
                            : `Pin ${title}`
                        }
                        title={conversation.isPinned ? "Unpin" : "Pin"}>
                        <StarIcon
                          className={classNames(
                            "h-4 w-4",
                            conversation.isPinned
                              ? "text-yellow-400"
                              : "text-gray-300"
                          )}
                        />
                      </button>
                      <button
                        onClick={e => handleDelete(e, conversation.id)}
                        className={classNames(
                          "flex-shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity",
                          "hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        )}
                        aria-label={`Delete ${title}`}
                        title="Delete conversation">
                        <XMarkIcon className="h-4 w-4 text-gray-400" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Pagination */}
      {total > 0 && (
        <div className="border-t border-gray-200 px-4 py-2">
          <IndependentPagination
            data={filteredAndSorted}
            page={page}
            pageSize={pageSize}
            pageSizes={[5, 10, 20, 50]}
            setPage={onPageChange}
            setPageSize={onPageSizeChange}
            total={total}
          />
        </div>
      )}

      <DeleteModal
        item={deleteTarget}
        setItem={setDeleteTarget}
        deleteFunction={handleConfirmDelete}
        isLoading={isDeleteLoading}
        title="Delete Conversation"
        message={name => (
          <>
            Are you sure you want to delete conversation{" "}
            <span className="font-semibold">{name}</span>?
          </>
        )}
        confirmText="Delete"
      />
    </div>
  );
}

// Trigger button component
export function ConversationListButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center justify-center p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
      <ListBulletIcon className="h-5 w-5" />
    </button>
  );
}
